import { TEAM_ROLE_LABEL } from '@greenroom/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Users } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'
import { Logo } from '../components/logo'
import { Button, LinkButton } from '../components/ui/button'
import { ErrorNote } from '../components/ui/misc'
import { FullPageSpinner } from '../components/ui/spinner'
import { api, errorMessage } from '../lib/api'
import { meQuery, selectTeam, useMe } from '../lib/auth'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'

export function InvitePage() {
  const { token = '' } = useParams()
  const me = useMe()
  const invite = useQuery(q.invite(token))
  const qc = useQueryClient()
  const navigate = useNavigate()
  useTitle(invite.data ? `Join ${invite.data.teamName}` : 'Team invite')
  const accept = useMutation({
    mutationFn: () => api<{ teamId: string }>(`/api/invites/${token}/accept`, { method: 'POST' }),
    meta: { silent: true },
    onSuccess: async ({ teamId }) => {
      await qc.invalidateQueries({ queryKey: meQuery.queryKey })
      selectTeam(teamId)
      navigate('/studios', { replace: true })
    },
  })

  if (me.isPending || invite.isPending) return <FullPageSpinner />
  const next = `?next=${encodeURIComponent(`/invite/${token}`)}`

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
      <Logo className="mb-10" />
      <div className="card w-full max-w-sm p-7 text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Users className="size-5" />
        </div>
        {invite.isError ? (
          <>
            <h1 className="text-lg font-semibold">This invite does not work</h1>
            <p className="mt-2 text-sm text-muted">{errorMessage(invite.error)}. Ask the team owner for a new link.</p>
          </>
        ) : invite.data.expired ? (
          <>
            <h1 className="text-lg font-semibold">This invite has expired</h1>
            <p className="mt-2 text-sm text-muted">It was used already or is older than a week. Ask {invite.data.invitedBy} for a new link.</p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold">Join {invite.data.teamName}</h1>
            <p className="mt-2 text-sm text-muted">
              {invite.data.invitedBy} invited you as {TEAM_ROLE_LABEL[invite.data.role].toLowerCase()}.
            </p>
            <div className="mt-6 space-y-2">
              {me.data ? (
                <>
                  <Button variant="primary" className="w-full" loading={accept.isPending} onClick={() => accept.mutate()}>
                    Join as {me.data.user.name}
                  </Button>
                  {accept.error && <ErrorNote>{errorMessage(accept.error)}</ErrorNote>}
                </>
              ) : (
                <>
                  <LinkButton to={`/signup${next}`} variant="primary" className="w-full">
                    Create an account
                  </LinkButton>
                  <LinkButton to={`/login${next}`} variant="ghost" className="w-full">
                    I already have an account
                  </LinkButton>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
